import React, {useCallback, useEffect, useState} from 'react';

const COOKIE_KEY_INVISIBLE = 'vandle_bottomStickyAppDownloadBannerInvisible';
const COOKIE_KEY_PLATFORM = 'vandle_platform';
// バナーを非表示にするスクロール量の閾値
const SCROLL_THRESHOLD_DISABLED = 100;

// window や document を参照する場合は、ビルド時のエラーを避けるため、条件分岐をするか useEffect 内で参照する必要がある
// https://www.gatsbyjs.com/docs/debugging-html-builds/
const isBrowser =
  typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * スクロール量によってバナーが非表示になるかどうかを判定する
 *
 * 一定以上スクロールするとバナーが表示されるようになる。
 *
 * @returns {boolean}
 */
const useIsInvisibleOnScroll = (): boolean => {
  const [isInvisibleOnScroll, setIsInvisibleOnScroll] = useState(true);
  const changeVisibilityByScroll = useCallback(() => {
    if (isBrowser) {
      if (window.scrollY > SCROLL_THRESHOLD_DISABLED) {
        setIsInvisibleOnScroll(false);
      } else {
        setIsInvisibleOnScroll(true);
      }
    }
  }, []);
  useEffect(() => {
    document.addEventListener('scroll', changeVisibilityByScroll);
    return () => {
      document.removeEventListener('scroll', changeVisibilityByScroll);
    };
  }, [changeVisibilityByScroll]);
  return isInvisibleOnScroll;
};

/**
 * アプリ内のブラウザで表示されているか否かを判定する
 *
 * platform=app が URL に含まれている場合、
 * もしくは相当する cookie が存在する場合にアプリ内で表示されていると見做す。
 *
 * WebViewの場合は UserAgent で判定可能だが、
 * 将来的に WebView 以外に移行する可能性もあるため、この判定方法を用意している。
 *
 * @returns {boolean}
 */
const useIsPlatformApp = (): boolean => {
  useEffect(() => {
    const url = new URL(document.location.href);
    if (url.search.includes('platform=app')) {
      document.cookie = `${COOKIE_KEY_PLATFORM}=app; path=/`;
    }
  }, []);
  return (
    isBrowser &&
    document.cookie.split(';').some(item => {
      return item.trim() === `${COOKIE_KEY_PLATFORM}=app`;
    })
  );
};

/**
 * バナーの非表示化を制御する
 *
 * 現在非表示にされているか否かと、非表示にするための関数を返す。
 * 非表示にすると cookie に保存され、一定時間非表示状態が続く。
 *
 * @returns {{hide: Function, isInvisible: boolean}}
 */
const useVisibilityControl = (): {hide: () => void; isInvisible: boolean} => {
  const hasInvisibleCookie =
    isBrowser &&
    document.cookie.split(';').some(item => {
      return item.trim().indexOf(`${COOKIE_KEY_INVISIBLE}=`) === 0;
    });
  const [isInvisible, setIsInvisible] = useState(hasInvisibleCookie);
  const hide = useCallback(() => {
    setIsInvisible(true);
    if (isBrowser) {
      // 24時間非表示にする
      document.cookie = `${COOKIE_KEY_INVISIBLE}=1; max-age=86400; path=/`;
    }
  }, []);
  return {hide, isInvisible};
};

type Props = {
  unifiedStoreUrl: string;
};

/**
 * 常にページ下部に固定表示されるアプリダウンロードバナー
 *
 * @param {Props} props - Props
 * @param {string} props.unifiedStoreUrl - 自動振り分けダウンロードURL
 * @returns {ReactElement}
 */
export default function BottomStickyAppDownloadBanner(props: Props) {
  const isInvisibleOnScroll = useIsInvisibleOnScroll();
  const isPlatformApp = useIsPlatformApp();
  const {isInvisible: isInvisibleByUser, hide} = useVisibilityControl();
  const isInVandleAppWebView =
    isBrowser && window.navigator.userAgent.toLowerCase().includes('vandle/');

  const isInvisible =
    isInvisibleOnScroll ||
    isInvisibleByUser ||
    isPlatformApp ||
    isInVandleAppWebView;

  return isInvisible ? null : (
    <div className="BottomStickyAppDownloadBanner">
      <button
        className="BottomStickyAppDownloadBanner_closeButton"
        type="button"
        onClick={hide}
      >
        <img alt="閉じる" height={24} src="/images/material/icon-close.svg" />
      </button>
      <img
        alt=""
        className="BottomStickyAppDownloadBanner_icon"
        height={33}
        src="/images/material/vandle-banner-icon.svg"
      />
      <div className="BottomStickyAppDownloadBanner_content">
        <p className="BottomStickyAppDownloadBanner_content_title">
          バンドルカード
        </p>
        <p className="BottomStickyAppDownloadBanner_content_body">
          1分で登録！すぐにお買い物
        </p>
      </div>
      <a
        className="BottomStickyAppDownloadBanner_downloadButton"
        href={props.unifiedStoreUrl}
        role="button"
      >
        ダウンロード
      </a>
    </div>
  );
}
